import {
    Container,
    Sprite,
    Texture,
    InteractionEvent,
    Point,
    NineSlicePlane,
    Rectangle,
} from 'pixi.js';

import { Property } from 'springroll';

import { LoadContext, Disposable, DisposableGroup } from "../../../utils";
import { Focusable, fonts, StyledBitmapText } from '../../../ui';
import { Settings } from '../../../settings';

const LEVELS = [0, 0.2, 0.4, 0.6, 0.8, 1];
const HelperPoint = new Point();

export class Slider extends Container implements Focusable
{
    private focusBox: NineSlicePlane = null;
    private label:StyledBitmapText;
    private icon:Sprite;
    private levels:Sprite;
    private track:Sprite;
    private ball:Sprite;
    private _minSlider:number;
    private _maxSlider:number;
    private _value:number;
    private settingListener:Disposable;
    private _setting:Property<number>;
    private keyListener:Disposable;
    private tempValueIndex:number;
    private dragOffset: Point;
    private dragID: number;

    public static preload()
    {
        LoadContext.current.add('Settings', './assets/accessibility/Settings{size}.json');
        LoadContext.current.add('SharedUI', './assets/shared/SharedUI{size}.json');
    }

    constructor(setting: Property<number>, label:string, icon:Texture)
    {
        super();

        this._setting = setting;

        this.label = new StyledBitmapText(label, fonts.SliderLabel);
        this.label.anchor.set(1, 0);
        this.label.position.set(-20, 50);
        this.addChild(this.label);

        this.icon = new Sprite(icon);
        this.icon.anchor.set(1, 0.5);
        this.icon.position.set(this.label.x - this.label.width - 15, this.label.y + this.label.height / 2);
        this.addChild(this.icon);

        const loader = LoadContext.current;
        this.levels = new Sprite(loader.getSpritesheet('Settings').textures['audiolevel']);
        this.levels.position.set( 0, 0 );
        this.addChild( this.levels );

        this.track = new Sprite(loader.getSpritesheet('Settings').textures['slider0001']);
        this.track.position.set( -2, 65 );
        this.addChild( this.track );
        this.track.interactive = true;
        this.track.hitArea = new Rectangle(-5, -this.levels.height, this.track.width + 10, 65 + this.track.height);
        this.track.cursor = 'pointer';
        this.track.on('pointerdown', this.startDrag, this);
        this.track.on('pointertap', (ev: InteractionEvent) => {
            ev.data.getLocalPosition(this.track, HelperPoint);
            const rounded = Math.round((HelperPoint.x / this.track.width) * 5) / 5;
            this.value = rounded;
        });
        // bubble rollover event up a level
        this.track.on('pointerover', (ev:any) => this.emit('pointerover', ev));
        this.track.on('pointerout', (ev:any) => this.emit('pointerout', ev));

        this.ball = new Sprite(loader.getSpritesheet('Settings').textures['slider0002']);
        this.ball.anchor.set(0.5);
        this.ball.position.y = 65 + this.track.height / 2;
        this.addChild(this.ball);
        // this.ball.interactive = true;
        // this.ball.cursor = 'pointer';

        this._minSlider = this.track.x + this.ball.width / 3;
        this._maxSlider = this.track.x + this.track.width - this.ball.width / 3;

        this.value = setting.value;
        this.settingListener = Disposable.fromPropertySubscription(setting, (value) => {
            this.value = value;
        });

        // after we are set up, make the focus -
        // allows subclasses to have their own size info or just override sprite
        setTimeout(this.createFocus.bind(this), 0);
    }

    // Focusable
    private createFocus()
    {
        const FRAME_WIDTH = 6;

        this.focusBox = new NineSlicePlane(LoadContext.current.getSpritesheet('SharedUI').textures['highlight_frame'], FRAME_WIDTH, FRAME_WIDTH, FRAME_WIDTH, FRAME_WIDTH);
        const {width, height} = this.getSize();
        // make box just a tad bigger than whatever the size is
        this.focusBox.width = width + 2;
        this.focusBox.height = height + 50;
        this.focusBox.position.set(-50, -this.focusBox.height / 2 + 50);
        this.addChild(this.focusBox);
        this.focusBox.visible = false;
    }

    protected getSize()
    {
        return {width: 300, height: 80};
    }

    private startDrag(ev: InteractionEvent)
    {
        // do a quick check to make sure we are close to the ball, just for good measure
        ev.data.getLocalPosition(this.ball, HelperPoint);
        if (Math.abs(HelperPoint.x) > this.ball.width) return;

        this.dragID = ev.data.identifier;
        this.dragOffset = new Point(this.ball.x - ev.data.global.x, 0);

        this.track.on('pointermove', this.updateDrag, this);
        this.track.on('pointerup', this.endDrag, this);
        this.track.on('pointercancel', this.endDrag, this);
        this.track.on('pointerupoutside', this.endDrag, this);
    }

    private updateDrag(ev: InteractionEvent)
    {
        if (ev.data.identifier !== this.dragID) return;

        const pos = ev.data.global.x + this.dragOffset.x;
        this.ball.x = Math.min(this._maxSlider, Math.max(pos, this._minSlider));
    }

    private endDrag(ev: InteractionEvent)
    {
        if (ev.data.identifier !== this.dragID) return;

        this.track.off('pointermove', this.updateDrag, this);
        this.track.off('pointerup', this.endDrag, this);
        this.track.off('pointercancel', this.endDrag, this);
        this.track.off('pointerupoutside', this.endDrag, this);

        const rounded = Math.round(((this.ball.x - this._minSlider) / (this._maxSlider - this._minSlider)) * 5) / 5;
        this.value = rounded;
    }

    public get value() { return this._value; }
    public set value(value)
    {
        var emitEvent: boolean = value !== this._value;
        this._setting.value = this._value = value;
        this.ball.x = (this._maxSlider - this._minSlider) * value + this._minSlider;

        if (this.keyListener)
        {
            this.keyListener.dispose();
            this.keyListener = null;
        }
        if (emitEvent)
        {
            this.emit('changed', value);
        }
    }

    public get focus() { return this.focusBox.visible; }
    public set focus(focused) {
        this.focusBox.visible = focused;
        if (focused)
        {
            this.emit('pointerover');
            this.emit('focused');
        }
        else
        {
            if (this.keyListener)
            {
                this.keyListener.destroy();
                this.keyListener = null;
            }
            this.emit('pointerout');
            this.emit('unfocused');
        }
    }

    public get focusVisible() { return this.focusBox.visible; }
    public set focusVisible(visible) {
        this.focusBox.visible = visible;
    }

    public activate()
    {
        // if dwell clicks are running, then we are assuming that keyboard entry isn't going to
        // work and will just cycle through the levels
        if (Settings.instance.enableDwellClicks.value)
        {
            if (!this.tempValueIndex)
            {
                this.tempValueIndex = LEVELS.indexOf(this.value);
            }
            this.tempValueIndex++;
            if (this.tempValueIndex >= LEVELS.length)
            {
                this.tempValueIndex = 0;
            }
            this.ball.x = (this._maxSlider - this._minSlider) * LEVELS[this.tempValueIndex] + this._minSlider;
            this.value = LEVELS[this.tempValueIndex];
            return;
        }
        // cancel set mode and save the value if active
        if (this.keyListener)
        {
            // setting value clears keyListener, so we don't have to
            this.value = LEVELS[this.tempValueIndex];
        }
        // otherwise activate set mode
        else
        {
            this.tempValueIndex = LEVELS.indexOf(this.value);
            if (this.tempValueIndex < 0)
            {
                this.tempValueIndex = LEVELS.indexOf(Math.round(this.value * 5) / 5);
            }
            this.keyListener = new DisposableGroup(
                Disposable.fromKey('left', null, () => {
                    this.tempValueIndex--;
                    if (this.tempValueIndex < 0)
                    {
                        this.tempValueIndex = 0;
                    }
                    this.ball.x = (this._maxSlider - this._minSlider) * LEVELS[this.tempValueIndex] + this._minSlider;
                }),
                Disposable.fromKey('right', null, () => {
                    this.tempValueIndex++;
                    if (this.tempValueIndex >= LEVELS.length)
                    {
                        this.tempValueIndex = LEVELS.length - 1;
                    }
                    this.ball.x = (this._maxSlider - this._minSlider) * LEVELS[this.tempValueIndex] + this._minSlider;
                })
            );
        }
    }

    destroy()
    {
        this.keyListener?.destroy();
        this.settingListener.destroy();
        super.destroy({children: true});
    }
}